<template>
  <el-dialog
    :visible="visible"
    :before-close="handleClose"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    width="800px"
    class="instagram-dm-test-dialog"
  >
    <div slot="title">DMテスト</div>

    <div v-loading="store.loading" class="dialog-content">
      <Message type="warning" class="info-message">
        キャンペーンアカウントの DM
        にテストコードを送信すると、事前に設定したシナリオの内容が返信されます。キャンペーン期間中は動作しません。
      </Message>

      <InstagramDmTestCodePanel
        v-if="codePanelState !== 'empty' && store.current"
        :current="store.current"
        :panel-state="codePanelState"
        :disabled="store.disabled"
        :remaining-minutes="remainingMinutes"
        @copy="handleCopy"
      />

      <InstagramDmTestIssueForm
        :lottery-result="store.form.lotteryResult"
        :expiration-minutes="store.form.expirationMinutes"
        :button-label="store.buttonLabel"
        :loading="store.submitting"
        :disabled="store.disabled || store.loading"
        @update:lottery-result="store.form.lotteryResult = $event"
        @update:expiration-minutes="store.form.expirationMinutes = $event"
        @issue="handleIssue"
      />
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">閉じる</el-button>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { computed, getCurrentInstance, watch } from 'vue'

import Message from '@/client/components/_atoms/Message.vue'
import { TrackingService } from '@/client/services'

import { useInstagramInstantwinPretestStore } from '../../hooks/instagramInstantwinPretestStore'
import { useInstagramDmTestCodeExpiry } from '../../hooks/useInstagramDmTestCodeExpiry'
import { getInstagramDmTestCodePanelState, isInstagramDmTestDisabled } from '../../utils/instagramDmTest'
import InstagramDmTestCodePanel from './InstagramDmTestCodePanel.vue'
import InstagramDmTestIssueForm from './InstagramDmTestIssueForm.vue'

interface Props {
  visible: boolean
  prizeId: number
  campaignStatus?: number
  hasRunningCampaignOnSameAccount?: boolean
  onRefresh: () => Promise<void> | void
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const store = useInstagramInstantwinPretestStore()

const { proxy } = getCurrentInstance() as {
  proxy: {
    $notify: (arg: { title: string; customClass: string; duration: number }) => void
  }
}

const isDisabled = computed(() =>
  isInstagramDmTestDisabled(props.campaignStatus, props.hasRunningCampaignOnSameAccount)
)
const currentRef = computed(() => store.current)
const visibleRef = computed(() => props.visible)
const { remainingMinutes, isExpired } = useInstagramDmTestCodeExpiry(currentRef, visibleRef)
const codePanelState = computed(() => getInstagramDmTestCodePanelState(store.current, store.disabled, isExpired.value))

watch(
  () => props.visible,
  async visible => {
    if (!visible) {
      store.reset()
      return
    }

    try {
      await store.load(props.prizeId, isDisabled.value)
    } catch {
      proxy.$notify({
        title: 'データ取得に失敗しました',
        customClass: 'danger',
        duration: 5000
      })
    }
  },
  { immediate: true }
)

watch(
  () => isDisabled.value,
  value => {
    store.setDisabled(value)
  }
)

function handleClose() {
  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|テストダイアログを閉じる')
  emit('close')
}

async function handleIssue() {
  const actionName = store.current ? '再発行' : '発行'
  TrackingService.sendEvent(`click:応募グループ一覧（IGIW）|テストコード${actionName}`)

  try {
    await store.issue()
    await props.onRefresh()
    proxy.$notify({
      title: 'テストコードを発行しました。',
      customClass: 'success',
      duration: 3000
    })
  } catch {
    proxy.$notify({
      title: store.errorMessage || 'テストコードの発行に失敗しました',
      customClass: 'danger',
      duration: 5000
    })

    if (store.shouldRefreshCampaignState) {
      await props.onRefresh()
    }
  }
}

async function handleCopy(code: string) {
  TrackingService.sendEvent('click:応募グループ一覧（IGIW）|テストコードコピー')

  try {
    await navigator.clipboard.writeText(code)
    proxy.$notify({
      title: 'テストコードをコピーしました',
      customClass: 'success',
      duration: 3000
    })
  } catch {
    proxy.$notify({
      title: 'テストコードのコピーに失敗しました',
      customClass: 'warning',
      duration: 5000
    })
  }
}
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

:deep(.el-dialog) {
  .el-dialog__header {
    padding: $spacing-unit * 2;
    font-size: 16px;
    color: $text-color-white !important;
    background-color: $background-dark-color;
  }

  .el-dialog__body {
    padding: $spacing-unit * 2;
  }

  .el-dialog__footer {
    padding: 0 $spacing-unit * 2 $spacing-unit * 2;
  }

  .el-dialog__title,
  .el-dialog__headerbtn .el-dialog__close {
    color: $text-color-white;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}

.info-message {
  margin-bottom: $spacing-unit * 2;
}
</style>
